import { useState, useCallback } from "react";
import { History, Loader2, MessageSquare, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cn, extractErrorMessage } from "@/lib/utils";
import { FeedbackRichText } from "@/components/feedback/FeedbackRichText";
import { FEEDBACK_STATUS_LABELS } from "@/shared/schemas/feedback.schema";
import type { FeedbackStatus } from "@/shared/schemas/feedback.schema";
import { FEEDBACK_MAX_IMAGES } from "@/shared/schemas/feedbackStatus";
import {
  FeedbackImagePickerButtons,
  FeedbackThumbGrid,
  formatFeedbackDate,
  parseFeedbackImages,
  uploadFeedbackImages,
} from "@/components/feedback/feedback-ui";
import { formatEngineerDisplayName, extractUserFacingResponse } from "./TechnicalResolutionCard";

interface FeedbackCommentItem {
  id: string;
  feedbackId: string;
  userId: string;
  comment: string;
  images?: string[] | any;
  oldStatus?: string | null;
  newStatus?: string | null;
  createdAt: string | Date;
  user?: {
    id: string;
    fullName?: string | null;
    email?: string | null;
    avatar?: string | null;
  } | null;
}

interface FeedbackCommentsTimelineProps {
  feedbackId: string;
  comments: FeedbackCommentItem[];
  onImageZoom?: (url: string) => void;
  /** Lớp định vị ô nhập dính đáy trên mobile (tránh thanh hành động / dock) */
  composerStickyClassName?: string;
}

const statusLabel = (s?: string | null) =>
  (s && FEEDBACK_STATUS_LABELS[s as FeedbackStatus]) || s || "";

/**
 * Lịch sử & bình luận (chuẩn EMS 4.3): sự kiện đổi trạng thái ("A chuyển Mới → Đang xử lý")
 * và bình luận theo thời gian; ảnh dạng thumbnail; ô nhập có đính kèm ảnh, dính đáy trên mobile.
 */
export function FeedbackCommentsTimeline({
  feedbackId,
  comments = [],
  onImageZoom,
  composerStickyClassName,
}: FeedbackCommentsTimelineProps) {
  const queryClient = useQueryClient();
  const [commentText, setCommentText] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const addCommentMutation = useMutation({
    mutationFn: async (vars: { id: string; comment: string; images: string[] }) =>
      trpcClient.feedback.addComment.mutate(vars),
    onSuccess: () => {
      toast.success("Đã gửi bình luận");
      setCommentText("");
      setImages([]);
      queryClient.invalidateQueries({ queryKey: ["feedback", feedbackId] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
    },
    onError: (err: any) => {
      toast.error("Không thể gửi bình luận: " + extractErrorMessage(err));
    },
  });

  const addFiles = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      try {
        const urls = await uploadFeedbackImages(files, FEEDBACK_MAX_IMAGES - images.length, "Ảnh bình luận");
        if (urls.length) setImages((prev) => [...prev, ...urls].slice(0, FEEDBACK_MAX_IMAGES));
      } finally {
        setIsUploading(false);
      }
    },
    [images.length],
  );

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items || [])
        .filter((it) => it.type.startsWith("image/"))
        .map((it) => it.getAsFile())
        .filter((f): f is File => !!f);
      if (files.length) {
        e.preventDefault();
        await addFiles(files);
      }
    },
    [addFiles],
  );

  const handleSubmit = () => {
    if (!commentText.trim() && images.length === 0) {
      toast.warning("Vui lòng nhập nội dung hoặc đính kèm ảnh");
      return;
    }
    addCommentMutation.mutate({
      id: feedbackId,
      comment: commentText.trim() || "(Đính kèm hình ảnh)",
      images,
    });
  };

  return (
    <section
      data-testid="fb-timeline"
      className="bg-white rounded-xl border border-slate-200 shadow-sm"
    >
      <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-slate-600" />
        <h3 className="text-base font-bold text-slate-800">Lịch sử & bình luận ({comments.length})</h3>
      </div>

      <ol className="p-4 sm:p-5 space-y-3">
        {comments.length === 0 && (
          <li className="text-center py-6 text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg">
            Chưa có bình luận nào.
          </li>
        )}
        {comments.map((item) => {
          const isStatusEvent = Boolean(item.oldStatus && item.newStatus);
          const authorName = formatEngineerDisplayName(
            item.user?.fullName || item.user?.email || item.userId || "Người dùng",
          );
          const text = extractUserFacingResponse(item.comment);
          const itemImages = parseFeedbackImages(item.images);
          return (
            <li
              key={item.id}
              data-testid={isStatusEvent ? "fb-timeline-status-event" : "fb-timeline-comment"}
              className={cn(
                "flex gap-3 rounded-lg border p-3 text-sm min-w-0",
                isStatusEvent ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200",
              )}
            >
              <div
                className={cn(
                  "h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold",
                  isStatusEvent ? "bg-slate-200 text-slate-700" : "bg-blue-100 text-blue-700",
                )}
              >
                {isStatusEvent ? <History className="h-4 w-4" /> : authorName.substring(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="font-semibold text-slate-800">{authorName}</span>
                  {isStatusEvent && (
                    <span className="text-slate-600">
                      chuyển {statusLabel(item.oldStatus)} → {statusLabel(item.newStatus)}
                    </span>
                  )}
                  <span className="text-xs text-slate-500 ml-auto">{formatFeedbackDate(item.createdAt)}</span>
                </div>
                {text && (
                  <FeedbackRichText text={text} className="mt-1 text-slate-700" />
                )}
                {itemImages.length > 0 && (
                  <FeedbackThumbGrid
                    images={itemImages}
                    onZoom={onImageZoom}
                    className="mt-2 grid-cols-3 sm:grid-cols-4"
                  />
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <div
        data-testid="fb-comment-composer"
        onPaste={handlePaste}
        className={cn(
          "border-t border-slate-200 bg-white p-3 sm:p-4 space-y-2 rounded-b-xl z-10",
          composerStickyClassName,
        )}
      >
        <Textarea
          data-testid="fb-comment-input"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="Viết bình luận..."
          className="min-h-[64px] text-sm resize-none"
        />
        <FeedbackThumbGrid
          images={images}
          isUploading={isUploading}
          className="grid-cols-3 sm:grid-cols-6"
          onRemove={(idx) => setImages((prev) => prev.filter((_, i) => i !== idx))}
        />
        <div className="flex items-center gap-2">
          <FeedbackImagePickerButtons
            testIdPrefix="fb-comment"
            onFiles={addFiles}
            isUploading={isUploading}
            disabled={isUploading || images.length >= FEEDBACK_MAX_IMAGES}
            desktopLabel={`Đính kèm ảnh (${images.length}/${FEEDBACK_MAX_IMAGES})`}
            className="flex-1 min-w-0"
          />
          <Button
            type="button"
            data-testid="fb-comment-submit"
            onClick={handleSubmit}
            disabled={(!commentText.trim() && images.length === 0) || addCommentMutation.isPending || isUploading}
            className="min-h-11 sm:min-h-9 bg-blue-600 hover:bg-blue-700 text-white gap-1.5 shrink-0"
          >
            {addCommentMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span>Gửi</span>
          </Button>
        </div>
      </div>
    </section>
  );
}
